import type { Milliseconds, Percentage } from '@lemonade-hq/ts-helpers';
import { Flex, spacing } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';

const SCROLLING_ID = `scroll-${Math.random().toString(36).substring(7)}`;
const EARLY_FINISH_PERCENTAGE: Percentage = 0.8;

export interface SlideYAnimatorProps {
  readonly animationKey?: string;
  readonly duration: Milliseconds;
  readonly translate: string;
  readonly onExitComplete?: () => void;
  readonly isTransitioningOut: boolean;
  readonly emptyComponent?: React.ReactNode;
}

export const SlideYAnimator: React.FC<PropsWithChildren<SlideYAnimatorProps>> = ({
  isTransitioningOut,
  children,
  animationKey,
  duration,
  translate: translateY,
  onExitComplete,
  emptyComponent,
}) => {
  useEffect(() => {
    const element = document.body;
    new ResizeObserver(entries => {
      const height = entries[0].contentRect.height;
      document.querySelector(`#${SCROLLING_ID}`)?.scrollTo({
        top: height,
        left: 0,
      });
    }).observe(element);
  }, []);

  return (
    <AnimatePresence mode="popLayout" onExitComplete={onExitComplete}>
      {animationKey != null ? (
        <Flex
          animate={{
            y: '0%',
            opacity: 1,
          }}
          as={motion.div}
          exit={
            isTransitioningOut
              ? {
                  y: translateY,
                  transition: {
                    type: 'tween',
                    duration: duration / 1000,
                    ease: [0.63, 0.32, 0.5, 0.96],
                    times: [0, EARLY_FINISH_PERCENTAGE],
                  },
                }
              : { opacity: 0 }
          }
          flexDirection="column"
          id={SCROLLING_ID}
          initial={{ y: '0%', opacity: 0 }}
          key={animationKey} // Use animationKey as the key to re-render on change
          transition={{ duration: duration / 1000, type: 'keyframes', ease: 'easeInOut' }}
          width="100%"
        >
          {children}
        </Flex>
      ) : (
        <Flex
          animate={{ opacity: 1 }}
          as={motion.div}
          initial={{ opacity: 0 }}
          marginBlockStart={spacing.s08}
          transition={{ delay: 0.2 }}
        >
          {emptyComponent}
        </Flex>
      )}
    </AnimatePresence>
  );
};
